import React, { useEffect, useState } from 'react';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const initialForm = { payroll_employee_id: '', description: '', principal_amount: '', emi_amount: '' };

// Loans/advances ledger - a tab on Employee Master, not its own sidebar
// entry, since a loan only makes sense against an existing payroll
// profile. `profiles` is passed down from PayrollEmployeesPage (already
// loaded there) rather than re-fetched here.
const PayrollLoansTab = ({ profiles }) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    payrollService.getLoans(statusFilter ? { status: statusFilter } : {})
      .then((res) => setLoans(res.data.loans || []))
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load loans', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.payroll_employee_id || !form.principal_amount || !form.emi_amount) {
      showToast('Employee, principal amount and EMI amount are required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await payrollService.createLoan({
        payroll_employee_id: Number(form.payroll_employee_id),
        description: form.description || null,
        principal_amount: Number(form.principal_amount),
        emi_amount: Number(form.emi_amount),
      });
      showToast('Loan added', 'success');
      setForm(initialForm);
      setShowAddForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add loan', 'error');
    }
    setSubmitting(false);
  };

  const handleClose = async (loan) => {
    if (!window.confirm(`Mark ${loan.employee_name}'s loan as closed? The payroll engine will stop deducting its EMI.`)) return;
    try {
      await payrollService.updateLoan(loan.id, { status: 'closed' });
      showToast('Loan closed', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to close loan', 'error');
    }
  };

  const handleDelete = async (loan) => {
    if (!window.confirm(`Delete this loan entry for ${loan.employee_name}? Only do this if it was entered by mistake.`)) return;
    try {
      await payrollService.deleteLoan(loan.id);
      showToast('Loan deleted', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete loan', 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {['active', 'closed', ''].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded font-semibold ${statusFilter === s ? 'bg-white text-komal-blue' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
            >
              {s ? s[0].toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold"
        >
          {showAddForm ? 'Cancel' : '+ Add Loan'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="glass-card p-6 mb-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select
              required
              value={form.payroll_employee_id}
              onChange={(e) => setForm({ ...form, payroll_employee_id: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select employee</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name} {p.employee_code ? `(${p.employee_code})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Personal advance" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Principal Amount (₹) *</label>
            <input required type="number" value={form.principal_amount} onChange={(e) => setForm({ ...form, principal_amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly EMI (₹) *</label>
            <input required type="number" value={form.emi_amount} onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
              {submitting ? 'Saving...' : 'Save Loan'}
            </button>
          </div>
        </form>
      )}

      <div className="glass-card">
        {loading ? (
          <p className="text-gray-500 text-sm p-5">Loading...</p>
        ) : (
          <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Employee</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium text-right">Principal</th>
                  <th className="px-4 py-2 font-medium text-right">Monthly EMI</th>
                  <th className="px-4 py-2 font-medium text-right">Outstanding</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Issued</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((l) => (
                  <tr key={l.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3 font-medium text-gray-900">{l.employee_name}</td>
                    <td className="px-4 py-3 text-gray-500">{l.description || '—'}</td>
                    <td className="px-4 py-3 text-right">{money(l.principal_amount)}</td>
                    <td className="px-4 py-3 text-right">{money(l.emi_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{money(l.outstanding_balance)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${l.status === 'active' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{l.issued_date}</td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {l.status === 'active' && (
                        <button onClick={() => handleClose(l)} className="text-amber-600 hover:underline text-xs font-semibold">Close</button>
                      )}
                      <button onClick={() => handleDelete(l)} className="text-red-600 hover:underline text-xs font-semibold">Delete</button>
                    </td>
                  </tr>
                ))}
                {loans.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">No loans in this view.</td></tr>
                )}
              </tbody>
            </table>
          </ScrollX>
        )}
      </div>
    </div>
  );
};

export default PayrollLoansTab;
